<script setup lang="ts">
  import { cva } from 'class-variance-authority'

  const props = withDefaults(
    defineProps<{
      showClose?: boolean
      title?: string
      hasOffset?: boolean
      sideY?: 'top' | 'bottom'
      sideX?: 'left' | 'right' | 'center'
      dialogContentClass?: string
      hasShadow?: boolean
      transition?: string
    }>(),
    {
      showClose: true,
      title: '',
      hasOffset: false,
      sideY: 'bottom',
      sideX: 'center',
      dialogContentClass: '',
      hasShadow: true,
      transition: ''
    }
  )

  const { isDesktop } = useBTBreakpoints()

  const isOpen = defineModel<boolean>()

  const transitionName = computed(() => {
    if (props.transition) {
      return props.transition
    }
    return isDesktop.value ? 'fade-in' : ''
  })

  const dialogContentBase = computed(() =>
    cva('fixed inset-0 z-50 flex h-auto flex-col md:rounded bg-white md:absolute md:z-40 overflow-hidden', {
      variants: {
        sideY: {
          top: 'md:top-auto',
          bottom: 'md:bottom-auto'
        },
        sideX: {
          left: 'md:left-auto',
          right: 'md:right-auto',
          center: ''
        },
        hasOffset: {
          true: 'mt-16',
          false: ''
        },
        dialogContentClass: {
          true: props.dialogContentClass,
          false: ''
        },
        hasShadow: {
          true: 'md:shadow-modal',
          false: ''
        }
      }
    })({
      sideY: props.sideY,
      sideX: props.sideX,
      hasOffset: props.hasOffset,
      dialogContentClass: !!props.dialogContentClass,
      hasShadow: props.hasShadow
    })
  )
</script>

<template>
  <DialogRoot v-model:open="isOpen">
    <DialogPortal disabled>
      <DialogOverlay class="md:fixed md:inset-0 md:z-30" />
      <Transition :name="transitionName">
        <DialogContent
          :class="dialogContentBase"
          :aria-describedby="undefined"
          tabindex="-1"
        >
          <div
            v-if="showClose"
            class="flex h-14 min-h-14 items-center gap-2 border-b border-border px-6 md:mb-4"
          >
            <DialogClose :aria-label="$t('aria.label.close')">
              <Icon
                class="text-bt-blue-dark"
                name="common/close"
              />
            </DialogClose>
          </div>

          <VisuallyHidden as-child>
            <DialogTitle v-if="title">
              {{ title }}
            </DialogTitle>
          </VisuallyHidden>

          <slot name="content" />
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<style>
  .fade-in-enter-active,
  .fade-in-out-enter-active,
  .fade-in-out-leave-active {
    transition: opacity 0.3s ease-out;
  }

  .fade-in-enter-from,
  .fade-in-leave-to,
  .fade-in-out-enter-from,
  .fade-in-out-leave-to {
    opacity: 0;
  }
</style>
